/** Extract / backfill YouTube watch + article URLs for Learning cells. */
import { GENERAL_VIDEOS, KNOWN_VIDEO_IDS, YT_FAMILIES, videoById } from "../constants/youtubePool";

const YT_RE = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]{6,}|youtu\.be\/[\w-]{6,})/i;
const YT_RE_G = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]{6,}|youtu\.be\/[\w-]{6,})[^\s)\]"']*/gi;
const HTTP_RE = /https?:\/\/[^\s<>"'`\[\]{}]+/gi;

const DEFAULT_ARTICLE = "https://www.freecodecamp.org/learn/";

export function youtubeId(url = "") {
  const m = String(url || "").match(/(?:v=|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : "";
}

export function extractResourceUrls(text) {
  const raw = String(text || "");
  const urls = raw.match(HTTP_RE) || [];
  const youtube = urls.find((u) => YT_RE.test(u)) || "";
  const article = urls.find(
    (u) =>
      !/youtube\.com|youtu\.be/i.test(u) &&
      !/geeksforgeeks\.org\/\?s=/i.test(u) &&
      !/\/search\?/i.test(u)
  ) || "";
  return { youtube, article };
}

/**
 * Text that names today's topic — labeled Concept / Step / Topic lines first.
 * Skips "Problem (short)" so words from the student's problem don't pick the video.
 */
export function topicFocusText(activity = "", content = "") {
  const lines = String(content || "")
    .replace(/\\n/g, "\n")
    .split("\n")
    .map((l) => l.replace(/^[▶•·\-\s]+/, "").trim())
    .filter(Boolean)
    .filter((l) => !/^(problem \(short\)|problem statement|problem|your problem|deliverable|connect|youtube|article|website)\s*:/i.test(l))
    .map((l) => l.replace(HTTP_RE, ""));
  const labeled = lines.filter((l) =>
    /^(concept|topic|one topic today|learn(?: today)?|what to learn|step|stage|subject|practice|leetcode|problem name|pattern|exercism|component|focus)\s*:/i.test(l)
  );
  const body = labeled.length ? labeled.join(" ") : lines.slice(0, 4).join(" ");
  return `${activity} ${body}`.trim();
}

export function familiesForTopic(topic = "") {
  const t = String(topic || "");
  return YT_FAMILIES.filter((f) => f.re.test(t));
}

export function pickFallbackLinks(topic = "", seed = 0) {
  const fam = familiesForTopic(topic)[0];
  const pool = fam?.videos?.length ? fam.videos : GENERAL_VIDEOS;
  const vid = pool[Math.abs(Number(seed) || 0) % pool.length];
  return {
    youtube: vid.url,
    youtubeTitle: `${vid.title} · ${vid.channel}`,
    article: fam?.article || DEFAULT_ARTICLE,
  };
}

export function isLearningActivity(activity = "") {
  return /learning|concept|subject|college|theory/i.test(String(activity || ""));
}

/** Learning, Project Build, coding, system design, and agent slots get video + article. */
export function isTechnicalResourceSlot(activity = "", content = "") {
  const blob = `${activity} ${String(content || "").slice(0, 220)}`;
  return /learning|concept|subject|college|theory|project|coding|dsa|leetcode|system design|architecture|hld|lld|agent workbench|ai agent|sns agent/i.test(
    blob
  );
}

function isHomeworkOrRest(activity = "") {
  return /homework|lunch|break|game|meditation|stand.?up|okr/i.test(String(activity || ""));
}

export function videoKey(row) {
  if (!Array.isArray(row)) return "";
  return `${String(row[0] || "").trim()}|${String(row[1] || "").trim()}|${String(row[2] || "").trim().toLowerCase()}`;
}

/**
 * One distinct, on-topic, verified video per technical slot across the whole plan.
 * Keeps an already-saved video only if it is in the verified pool and not used earlier.
 */
export function buildVideoPlan(rows = []) {
  const used = new Set();
  const plan = new Map();
  const list = Array.isArray(rows) ? rows : [];
  let n = 0;
  for (const row of list) {
    if (!Array.isArray(row)) continue;
    const activity = String(row[2] || "");
    const content = String(row[3] || "");
    if (isHomeworkOrRest(activity) || !isTechnicalResourceSlot(activity, content)) continue;
    n += 1;
    const focus = topicFocusText(activity, content);
    const existingId = youtubeId(extractResourceUrls(content).youtube);
    let pick = null;
    if (existingId && KNOWN_VIDEO_IDS.has(existingId) && !used.has(existingId)) {
      const fams = familiesForTopic(focus);
      const onTopic = !fams.length || fams.some((f) => f.videos.some((x) => x.id === existingId));
      if (onTopic) pick = videoById(existingId);
    }
    const fams = familiesForTopic(focus);
    if (!pick) {
      for (const f of fams) {
        const start = n % f.videos.length;
        for (let i = 0; i < f.videos.length && !pick; i++) {
          const cand = f.videos[(start + i) % f.videos.length];
          if (!used.has(cand.id)) pick = cand;
        }
        if (pick) break;
      }
    }
    if (!pick) {
      const pool = [...GENERAL_VIDEOS, ...YT_FAMILIES.flatMap((f) => f.videos)];
      pick = pool.find((x) => !used.has(x.id)) || pool[n % pool.length];
    }
    used.add(pick.id);
    plan.set(videoKey(row), {
      youtube: pick.url,
      youtubeTitle: `${pick.title} · ${pick.channel}`,
      article: fams[0]?.article || "",
    });
  }
  return plan;
}

/** Hide raw YouTube/Article URL lines when chips already show the links. */
export function stripResourceUrlLines(text = "") {
  return String(text || "")
    .split(/\n/)
    .filter((line) => {
      const t = line.trim();
      if (!t) return true;
      if (/▶?\s*(YouTube|Article|Website|Video)\s*:/i.test(t) && /https?:\/\//i.test(t)) return false;
      if (/^https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)\//i.test(t)) return false;
      return true;
    })
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * If a technical slot has no watch URL / article, append them.
 * `assigned` (from buildVideoPlan) replaces any repeated / unverified video in the cell.
 */
export function withLearningResources(activity, content, assigned = null) {
  let text = String(content || "").replace(/\\n/g, "\n");
  if (!isTechnicalResourceSlot(activity, text) || isHomeworkOrRest(activity)) {
    const found = extractResourceUrls(text);
    return { text, ...found, injected: false };
  }
  const found = extractResourceUrls(text);
  const fb = assigned || pickFallbackLinks(topicFocusText(activity, text));
  const youtube = fb.youtube || found.youtube;
  if (found.youtube && youtube && youtubeId(found.youtube) !== youtubeId(youtube)) {
    text = text.replace(YT_RE_G, youtube);
  }
  const article = found.article || fb.article || DEFAULT_ARTICLE;
  const hasYt = YT_RE.test(text);
  const extras = [];
  if (!hasYt && youtube) extras.push(`▶ YouTube: ${youtube}`);
  if (!found.article && article) extras.push(`▶ Article: ${article}`);
  let next = text;
  if (extras.length) {
    next = /Deliverable:|Done when:/i.test(text)
      ? text.replace(/(▶\s*)?(Deliverable|Done when):/i, `${extras.join("\n")}\n▶ $2:`)
      : `${text.trim()}\n${extras.join("\n")}`;
  }
  return {
    text: next,
    youtube,
    youtubeTitle: fb.youtubeTitle || "",
    article,
    injected: extras.length > 0,
  };
}
